<template>
    <div class="create-book-container">
      <div class="page-header">
        <div class="header-with-back">
          <button @click="goBackToBooks" class="back-button">
            <font-awesome-icon :icon="['fas', 'arrow-left']" /> Kembali ke Daftar Buku
          </button>
          <div>
            <h2>{{ isEditMode ? 'Edit Buku' : 'Buat Buku Baru' }}</h2>
            <p class="subtitle">{{ isEditMode ? 'Update informasi buku Anda' : 'Isi detail untuk buku baru Anda' }}</p>
          </div>
        </div>
      </div>
  
      <div class="form-container">
        <!-- Book Creation Form -->
        <form @submit.prevent="saveBook" class="book-form">
          <!-- Book Cover Upload -->
          <div class="cover-upload-section">
            <div class="cover-preview">
              <img :src="coverPreview || placeholderImage" alt="Book Cover Preview" class="cover-image" />
              <div class="upload-overlay">
                <font-awesome-icon :icon="['fas', 'camera']" class="upload-icon" />
                <span>{{ coverPreview ? 'Ganti Cover' : 'Upload Cover' }}</span>
              </div>
              <input type="file" ref="coverInput" @change="handleCoverUpload" accept="image/*" class="cover-input" />
            </div>
            <p class="cover-hint">Ukuran ideal: 400x600 piksel (rasio 2:3)</p>
          </div>
  
          <!-- Book Details Section -->
          <div class="form-content">
            <!-- Book Title -->
            <div class="form-group">
              <label for="bookTitle">Judul Buku <span class="required">*</span></label>
              <input 
                type="text" 
                id="bookTitle" 
                v-model="bookData.title" 
                required 
                placeholder="Masukkan judul buku"
                :class="{'error-input': errors.title}"
              />
              <span v-if="errors.title" class="error-message">{{ errors.title }}</span>
            </div>
  
            <!-- Book Type -->
            <div class="form-group">
              <label>Jenis Buku <span class="required">*</span></label>
              <div class="radio-group">
                <label class="radio-item">
                  <input type="radio" v-model="bookData.fiction" :value="true" /> Fiksi
                </label>
                <label class="radio-item">
                  <input type="radio" v-model="bookData.fiction" :value="false" /> Non-Fiksi
                </label>
              </div>
            </div>
  
            <!-- Categories -->
            <div class="form-group">
              <label for="categories">Kategori <span class="required">*</span> (Maksimal 3)</label>
              <div class="categories-selection">
                <div v-if="loadingCategories" class="loading-categories">
                  <font-awesome-icon :icon="['fas', 'spinner']" spin /> Memuat kategori...
                </div>
                <div v-else>
                  <div class="selected-categories">
                    <div v-for="(categoryId, index) in bookData.categories" :key="'selected-'+categoryId" class="selected-category">
                      {{ getCategoryName(categoryId) }}
                      <button type="button" @click="removeCategory(index)" class="remove-category">
                        <font-awesome-icon :icon="['fas', 'times']" />
                      </button>
                    </div>
                  </div>
                  <div class="category-selector" v-if="bookData.categories.length < 3">
                    <select v-model="selectedCategory" @change="addCategory" class="category-select">
                      <option value="">-- Pilih Kategori --</option>
                      <optgroup v-if="bookData.fiction" label="Fiksi">
                        <option v-for="category in fictionCategories" :key="category.id" :value="category.id">
                          {{ category.name }}
                        </option>
                      </optgroup>
                      <optgroup v-else label="Non-Fiksi">
                        <option v-for="category in nonFictionCategories" :key="category.id" :value="category.id">
                          {{ category.name }}
                        </option>
                      </optgroup>
                    </select>
                  </div>
                </div>
                <span v-if="errors.categories" class="error-message">{{ errors.categories }}</span>
              </div>
            </div>
  
            <!-- Year Published -->
            <div class="form-group">
              <label for="yearPublished">Tahun Publikasi <span class="required">*</span></label>
              <input 
                type="number" 
                id="yearPublished" 
                v-model="bookData.year_published" 
                required 
                min="1900"
                :max="currentYear"
                placeholder="Tahun publikasi buku"
                :class="{'error-input': errors.year_published}"
              />
              <span v-if="errors.year_published" class="error-message">{{ errors.year_published }}</span>
            </div>
  
            <!-- Tags -->
            <div class="form-group">
              <label for="tags">Tag (pisahkan dengan koma)</label>
              <input 
                type="text" 
                id="tags" 
                v-model="bookData.tags" 
                placeholder="Contoh: petualangan, misteri, sejarah"
              />
            </div>
  
            <!-- Synopsis -->
            <div class="form-group">
              <label for="synopsis">Sinopsis <span class="required">*</span></label>
              <TinyMceEditor
                v-model="bookData.synopsis"
                :placeholder="'Tuliskan sinopsis atau ringkasan buku Anda'"
                :class="{'error-input': errors.synopsis}"
                :height="250"
                />
              <span v-if="errors.synopsis" class="error-message">{{ errors.synopsis }}</span>
            </div>
  
            <!-- Submit/Cancel Buttons -->
            <div class="form-actions">
              <button type="button" @click="goBackToBooks" class="btn-cancel">Batal</button>
              <button 
                type="submit" 
                class="btn-primary" 
                :disabled="isSubmitting || !isFormValid"
              >
                <font-awesome-icon v-if="isSubmitting" :icon="['fas', 'spinner']" spin />
                {{ isSubmitting ? 'Menyimpan...' : (isEditMode ? 'Perbarui Buku' : 'Simpan Buku') }}
              </button>
            </div>
          </div>
        </form>
  
        <!-- Chapters Section (Only shown after book is saved) -->
        <div v-if="savedBookId && !isSubmitting" class="chapters-section">
          <h3>Daftar Bab</h3>
          
          <div v-if="loadingChapters" class="loading-chapters">
            <font-awesome-icon :icon="['fas', 'spinner']" spin /> Memuat daftar bab...
          </div>
          
          <div v-else>
            <div v-if="chapters.length === 0" class="no-chapters">
              <p>Belum ada bab untuk buku ini.</p>
            </div>
            
            <div v-else class="chapters-list">
              <div v-for="chapter in sortedChapters" :key="chapter.id" class="chapter-item">
                <div class="chapter-details">
                  <span class="chapter-number">Bab {{ chapter.order_number }}:</span>
                  <span class="chapter-title">{{ chapter.title }}</span>
                  <span class="chapter-stats">
                    <font-awesome-icon :icon="['fas', 'file-alt']" /> {{ chapter.word_count }} kata | 
                    <font-awesome-icon :icon="['fas', 'clock']" /> {{ chapter.estimated_read_time }} menit
                  </span>
                </div>
                <div class="chapter-actions">
                  <button @click="editChapter(chapter)" class="btn-edit">
                    <font-awesome-icon :icon="['fas', 'edit']" />
                  </button>
                  <button @click="confirmDeleteChapter(chapter)" class="btn-delete">
                    <font-awesome-icon :icon="['fas', 'trash']" />
                  </button>
                </div>
              </div>
            </div>
            
            <button @click="addNewChapter" class="btn-add-chapter">
              <font-awesome-icon :icon="['fas', 'plus']" /> Tambah Bab Baru
            </button>
          </div>
        </div>
      </div>
  
      <!-- Confirmation Modal -->
      <div v-if="showConfirmModal" class="modal-overlay" @click.self="cancelConfirmation">
        <div class="modal-content confirm-modal">
          <div class="modal-header">
            <h3>{{ confirmTitle }}</h3>
            <button @click="cancelConfirmation" class="close-btn">
              <font-awesome-icon :icon="['fas', 'times']" />
            </button>
          </div>
          <div class="modal-body">
            <p class="confirm-message">{{ confirmMessage }}</p>
          </div>
          <div class="modal-actions">
            <button @click="cancelConfirmation" class="btn-cancel">Batal</button>
            <button @click="confirmAction" class="btn-confirm" :class="confirmButtonClass">
              <font-awesome-icon v-if="isConfirming" :icon="['fas', 'spinner']" spin />
              {{ isConfirming ? 'Memproses...' : confirmButtonText }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import placeholderImage from '@/assets/placeholder.svg';
  import TinyMceEditor from '@/components/SimpleHtmlEditor.vue';
  import Swal from 'sweetalert2';
  
  export default {
    name: 'CreateBook',
    components: {
      TinyMceEditor
    },
    props: {
      editBookId: {
        type: [Number, String],
        default: null
      },
      editBookSlug: {
        type: String,
        default: null
      }
    },
    data() {
      return {
        // Book data
        bookData: {
          title: '',
          fiction: true,
          categories: [],
          year_published: new Date().getFullYear(),
          tags: '',
          synopsis: '',
          is_published: false
        },
        coverFile: null,
        coverPreview: null,
        placeholderImage: placeholderImage,
        currentYear: new Date().getFullYear(),
        
        // Categories data
        fictionCategories: [],
        nonFictionCategories: [],
        selectedCategory: '',
        loadingCategories: false,
        
        // Form state
        errors: {},
        isSubmitting: false,
        isEditMode: false,
        savedBookId: null,
        savedBookSlug: null,
        
        // Chapters data
        chapters: [],
        loadingChapters: false,
        
        // Confirmation modal
        showConfirmModal: false,
        confirmTitle: '',
        confirmMessage: '',
        confirmButtonText: '',
        confirmButtonClass: '',
        confirmAction: null,
        isConfirming: false,
        itemToDelete: null,
        
        // Debug mode
        debugMode: false
      };
    },
    computed: {
      isFormValid() {
        return (
          this.bookData.title.trim() !== '' && 
          this.bookData.synopsis.trim() !== '' && 
          this.bookData.categories.length > 0 &&
          this.bookData.year_published >= 1900 &&
          this.bookData.year_published <= this.currentYear
        );
      },
      sortedChapters() {
        return [...this.chapters].sort((a, b) => a.order_number - b.order_number);
      }
    },
    async created() {
      // Load categories
      this.loadCategories();
      
      // Check if we're in edit mode via props or route params
      const routeBookId = this.$route.params.bookId;
      const routeBookSlug = this.$route.params.bookSlug;
      
      if (this.editBookId || this.editBookSlug || routeBookId || routeBookSlug) {
        this.isEditMode = true;
        this.savedBookId = this.editBookId || routeBookId;
        this.savedBookSlug = this.editBookSlug || routeBookSlug;
        
        console.log('Loading book details for:', {
          id: this.savedBookId,
          slug: this.savedBookSlug
        });
        
        await this.loadBookDetails();
        await this.loadChapters();
      }
    },
    methods: {
      goBackToBooks() {
        this.$emit('cancel');
      },
      
      async loadCategories() {
        this.loadingCategories = true;
        try {
          // Load fiction categories
          const fictionResponse = await axios.get(
            `${process.env.VUE_APP_API_BASE_URL}/api/book-categories/fiction/`
          );
          this.fictionCategories = fictionResponse.data;
          
          // Load non-fiction categories
          const nonFictionResponse = await axios.get(
            `${process.env.VUE_APP_API_BASE_URL}/api/book-categories/non-fiction/`
          );
          this.nonFictionCategories = nonFictionResponse.data;
          
        } catch (error) {
          console.error('Error loading categories:', error);
          this.showNotification('error', 'Gagal memuat kategori buku');
        } finally {
          this.loadingCategories = false;
        }
      },
      
      async loadBookDetails() {
        try {
          const token = localStorage.getItem('token');
          if (!token) {
            this.$router.push('/login');
            return;
          }
          
          const endpoint = this.savedBookSlug 
            ? `${process.env.VUE_APP_API_BASE_URL}/api/books/${this.savedBookSlug}/`
            : `${process.env.VUE_APP_API_BASE_URL}/api/books/${this.savedBookId}/`;
          
          if (this.debugMode) {
            console.log('Loading book from endpoint:', endpoint);
          }
          
          const response = await axios.get(endpoint, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          const book = response.data;
          
          if (this.debugMode) {
            console.log('Loaded book data:', book);
          }
          
          // Update bookData with response
          this.bookData = {
            title: book.title || '',
            fiction: book.fiction !== undefined ? book.fiction : true,
            categories: book.categories || [],
            year_published: book.year_published || new Date().getFullYear(),
            tags: book.tags || '',
            synopsis: book.synopsis || '',
            is_published: book.is_published || false
          };
          
          // Set saved book ID and slug
          this.savedBookId = book.id;
          this.savedBookSlug = book.slug;
          
          // Set cover preview if available
          if (book.cover) {
            this.coverPreview = book.cover;
          }
          
        } catch (error) {
          console.error('Error loading book details:', error);
          if (error.response) {
            console.error('Error response:', error.response.data);
            console.error('Status:', error.response.status);
          }
          this.showNotification('error', 'Gagal memuat data buku');
          this.$emit('cancel');
        }
      },
      
      async loadChapters() {
        if (!this.savedBookId && !this.savedBookSlug) return;
        
        this.loadingChapters = true;
        try {
          const token = localStorage.getItem('token');
          
          const response = await axios.get(
            `${process.env.VUE_APP_API_BASE_URL}/api/books/${this.savedBookSlug}/`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
          
          this.chapters = response.data.chapters || [];
          
        } catch (error) {
          console.error('Error loading chapters:', error);
          this.showNotification('error', 'Gagal memuat daftar bab buku');
        } finally {
          this.loadingChapters = false;
        }
      },
      
      handleCoverUpload(event) {
        const file = event.target.files[0];
        if (!file) return;
        
        if (!file.type.includes('image/')) {
          this.showNotification('error', 'Silakan pilih file gambar');
          return;
        }
        
        this.coverFile = file;
        this.coverPreview = URL.createObjectURL(file);
      },
      
      addCategory() {
        if (!this.selectedCategory) return;
        
        if (this.bookData.categories.includes(parseInt(this.selectedCategory))) {
          this.showNotification('info', 'Kategori ini sudah dipilih');
          return;
        }
        
        this.bookData.categories.push(parseInt(this.selectedCategory));
        this.selectedCategory = '';
        
        if (this.errors.categories) {
          this.errors.categories = '';
        }
      },
      
      removeCategory(index) {
        this.bookData.categories.splice(index, 1);
      },
      
      getCategoryName(categoryId) {
        const allCategories = [...this.fictionCategories, ...this.nonFictionCategories];
        const category = allCategories.find(c => c.id === categoryId);
        return category ? category.name : 'Unknown Category';
      },
      
      async saveBook() {
        this.errors = {};
        
        if (!this.bookData.title.trim()) {
          this.errors.title = 'Judul buku harus diisi';
          return;
        }
        
        if (!this.bookData.synopsis.trim()) {
          this.errors.synopsis = 'Sinopsis buku harus diisi';
          return;
        }
        
        if (this.bookData.categories.length === 0) {
          this.errors.categories = 'Pilih setidaknya satu kategori';
          return;
        }
        
        if (this.bookData.year_published < 1900 || this.bookData.year_published > this.currentYear) {
          this.errors.year_published = `Tahun harus antara 1900 dan ${this.currentYear}`;
          return;
        }
        
        this.isSubmitting = true;
        
        try {
          const token = localStorage.getItem('token');
          if (!token) {
            this.$router.push('/login');
            return;
          }
          
          const formData = new FormData();
          formData.append('title', this.bookData.title);
          formData.append('fiction', this.bookData.fiction);
          formData.append('year_published', this.bookData.year_published);
          formData.append('tags', this.bookData.tags);
          formData.append('synopsis', this.bookData.synopsis);
          formData.append('is_published', this.bookData.is_published);
          
          const authorProfileId = localStorage.getItem('author_profile_id');
          if (authorProfileId) {
            formData.append('author_profile', authorProfileId);
            
            if (this.debugMode) {
              console.log('Using author_profile_id:', authorProfileId);
            }
          } else {
            console.error('author_profile_id not found in localStorage');
            this.showNotification('error', 'ID profil penulis tidak ditemukan. Silakan login ulang.');
            return;
          }
          
          this.bookData.categories.forEach(categoryId => {
            formData.append('categories', categoryId);
          });
          
          if (this.coverFile) {
            formData.append('cover', this.coverFile);
          }
          
          let response;
          
          if (this.isEditMode) {
            response = await axios.put(
              `${process.env.VUE_APP_API_BASE_URL}/api/books/${this.savedBookSlug}/`,
              formData,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'multipart/form-data'
                }
              }
            );
          } else {
            response = await axios.post(
              `${process.env.VUE_APP_API_BASE_URL}/api/books/`,
              formData,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'multipart/form-data'
                }
              }
            );
          }
          
          this.savedBookId = response.data.id;
          this.savedBookSlug = response.data.slug;
          
          if (!this.isEditMode) {
            this.isEditMode = true;
          }
          
          this.loadChapters();
          
          this.showNotification('success', this.isEditMode ? 'Buku berhasil diperbarui' : 'Buku berhasil dibuat');
          
          this.$emit('book-saved', {
            id: this.savedBookId,
            slug: this.savedBookSlug
          });
          
        } catch (error) {
          console.error('Error saving book:', error);
          
          if (error.response?.data) {
            const responseData = error.response.data;
            
            Object.keys(responseData).forEach(key => {
              if (typeof responseData[key] === 'string') {
                this.errors[key] = responseData[key];
              } else if (Array.isArray(responseData[key])) {
                this.errors[key] = responseData[key][0];
              }
            });
          }
          
          this.showNotification('error', 'Gagal menyimpan buku. Perikse form Anda.');
        } finally {
          this.isSubmitting = false;
        }
      },
      
      cancel() {
        this.goBackToBooks();
      },
      
      confirmCancel() {
        this.$emit('cancel');
      },
      
      isFormModified() {
        return (
          this.bookData.title !== '' ||
          this.bookData.categories.length > 0 ||
          this.bookData.synopsis !== '' ||
          this.bookData.tags !== '' ||
          this.coverFile !== null
        );
      },
      
      addNewChapter() {
        this.$router.push({
          name: 'CreateChapter',
          params: {
            bookSlug: this.savedBookSlug,
            bookId: this.savedBookId
          },
          query: {
            create: 'true',
            nextOrder: this.getNextChapterOrder(),
            fromCreateBook: 'true'
          }
        });
      },
      
      getNextChapterOrder() {
        if (this.chapters.length === 0) return 1;
        const maxOrder = Math.max(...this.chapters.map(c => c.order_number));
        return maxOrder + 1;
      },
      
      editChapter(chapter) {
        this.$router.push({
          name: 'ChapterEditor',
          params: {
            bookSlug: this.savedBookSlug,
            chapterSlug: chapter.slug
          },
          query: {
            fromCreateBook: 'true'
          }
        });
      },
      
      confirmDeleteChapter(chapter) {
        this.confirmTitle = 'Hapus Bab?';
        this.confirmMessage = `Apakah Anda yakin ingin menghapus bab "${chapter.title}"? Tindakan ini tidak dapat dibatalkan.`;
        this.confirmButtonText = 'Hapus';
        this.confirmButtonClass = 'btn-delete';
        this.confirmAction = () => this.deleteChapter(chapter);
        this.itemToDelete = chapter;
        this.showConfirmModal = true;
      },
      
      async deleteChapter(chapter) {
        this.isConfirming = true;
        
        try {
          const token = localStorage.getItem('token');
          
          await axios.delete(
            `${process.env.VUE_APP_API_BASE_URL}/api/chapters/${chapter.slug}/`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
          
          this.chapters = this.chapters.filter(c => c.id !== chapter.id);
          
          this.showNotification('success', 'Bab berhasil dihapus');
          this.showConfirmModal = false;
          
        } catch (error) {
          console.error('Error deleting chapter:', error);
          this.showNotification('error', 'Gagal menghapus bab. Silakan coba lagi.');
        } finally {
          this.isConfirming = false;
        }
      },
      
      showNotification(type, message) {
        Swal.fire({
          icon: type,
          title: type === 'success' ? 'Berhasil!' : type === 'error' ? 'Error!' : 'Perhatian',
          text: message,
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true
        });
      }
    }
  };
  </script>
  
  <style scoped>
  /* All existing styles remain the same */
  .create-book-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
  }
  
  .page-header {
    text-align: center;
    margin-bottom: 30px;
  }
  
  .header-with-back {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    gap: 20px;
  }
  
  .back-button {
    display: flex;
    align-items: center;
    gap: 8px;
    background-color: #f1f1f1;
    color: #333;
    border: none;
    padding: 10px 15px;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.3s;
  }
  
  .back-button:hover {
    background-color: #e0e0e0;
  }
  
  .page-header h2 {
    font-size: 28px;
    color: #333;
    margin-bottom: 10px;
  }
  
  .subtitle {
    color: #666;
    font-size: 16px;
  }
  
  .form-container {
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }
  
  .book-form {
    display: flex;
    padding: 30px;
  }
  
  .cover-upload-section {
    width: 250px;
    margin-right: 30px;
    flex-shrink: 0;
  }
  
  .form-content {
    flex: 1;
  }
  
  .cover-preview {
    position: relative;
    width: 100%;
    height: 350px;
    border: 2px dashed #ddd;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    margin-bottom: 10px;
  }
  
  .cover-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .upload-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    opacity: 0;
    transition: opacity 0.3s;
    color: white;
  }
  
  .cover-preview:hover .upload-overlay {
    opacity: 1;
  }
  
  .upload-icon {
    font-size: 24px;
    margin-bottom: 10px;
  }
  
  .cover-input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }
  
  .cover-hint {
    font-size: 12px;
    color: #777;
    text-align: center;
  }
  
  .form-group {
    margin-bottom: 20px;
  }
  
  .form-group label {
    display: block;
    font-weight: 600;
    margin-bottom: 8px;
    color: #333;
  }
  
  .form-group input,
  .form-group textarea,
  .form-group select {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 16px;
  }
  
  .form-group textarea {
    resize: vertical;
  }
  
  .radio-group {
    display: flex;
    gap: 20px;
  }
  
  .radio-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: normal;
    cursor: pointer;
  }
  
  .radio-item input {
    width: auto;
    cursor: pointer;
  }
  
  .required {
    color: #e74c3c;
  }
  
  .error-input {
    border-color: #e74c3c !important;
  }
  
  .error-message {
    display: block;
    color: #e74c3c;
    font-size: 14px;
    margin-top: 5px;
  }
  
  .input-hint {
    display: block;
    font-size: 12px;
    color: #777;
    margin-top: 5px;
  }
  
  .categories-selection {
    margin-top: 10px;
  }
  
  .selected-categories {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 10px;
  }
  
  .selected-category {
    background-color: #488e71;
    color: white;
    padding: 8px 12px;
    border-radius: 20px;
    display: flex;
    align-items: center;
    gap: 5px;
  }
  
  .remove-category {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 0;
    font-size: 14px;
  }
  
  .category-select {
    width: 100%;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 16px;
  }
  
  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 15px;
    margin-top: 30px;
  }
  
  .btn-primary {
    background-color: #488e71;
    color: white;
    border: none;
    padding: 12px 25px;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .btn-primary:hover {
    background-color: #3a7259;
  }
  
  .btn-primary:disabled {
    background-color: #93c3b1;
    cursor: not-allowed;
  }
  
  .btn-cancel {
    background-color: #f1f1f1;
    color: #333;
    border: none;
    padding: 12px 25px;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
  }
  
  .btn-cancel:hover {
    background-color: #e9e9e9;
  }
  
  .chapters-section {
    padding: 30px;
    border-top: 1px solid #eee;
  }
  
  .chapters-section h3 {
    margin-top: 0;
    margin-bottom: 20px;
    color: #333;
  }
  
  .chapters-list {
    margin-bottom: 20px;
  }
  
  .chapter-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    background-color: #f9f9f9;
    border-radius: 6px;
    margin-bottom: 10px;
  }
  
  .chapter-details {
    flex: 1;
  }
  
  .chapter-number {
    font-weight: 600;
    color: #488e71;
  }
  
  .chapter-title {
    margin-left: 10px;
    font-weight: 500;
  }
  
  .chapter-stats {
    display: block;
    font-size: 14px;
    color: #777;
    margin-top: 5px;
  }
  
  .chapter-actions {
    display: flex;
    gap: 10px;
  }
  
  .btn-edit,
  .btn-delete {
    background: none;
    border: none;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }
  
  .btn-edit {
    color: #488e71;
    background-color: rgba(72, 142, 113, 0.1);
  }
  
  .btn-delete {
    color: #e74c3c;
    background-color: rgba(231, 76, 60, 0.1);
  }
  
  .btn-edit:hover {
    background-color: rgba(72, 142, 113, 0.2);
  }
  
  .btn-delete:hover {
    background-color: rgba(231, 76, 60, 0.2);
  }
  
  .btn-add-chapter {
    background-color: #488e71;
    color: white;
    border: none;
    padding: 12px 20px;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 20px;
  }
  
  .btn-add-chapter:hover {
    background-color: #3a7259;
  }
  
  .no-chapters {
    text-align: center;
    padding: 30px;
    color: #777;
    background-color: #f9f9f9;
    border-radius: 6px;
  }
  
  .loading-chapters {
    text-align: center;
    color: #777;
    padding: 20px;
  }
  
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  
  .modal-content {
    background-color: white;
    border-radius: 8px;
    width: 90%;
    max-width: 500px;
    padding: 25px;
    position: relative;
  }
  
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }
  
  .modal-header h3 {
    margin: 0;
    color: #333;
  }
  
  .close-btn {
    background: none;
    border: none;
    font-size: 20px;
    cursor: pointer;
    color: #777;
  }
  
  .btn-confirm {
    background-color: #e74c3c;
    color: white;
    border: none;
    padding: 12px 25px;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .btn-confirm:hover {
    background-color: #c0392b;
  }
  
  @media (max-width: 768px) {
    .book-form {
      flex-direction: column;
      padding: 20px;
    }
    
    .cover-upload-section {
      width: 100%;
      margin-right: 0;
      margin-bottom: 20px;
    }
    
    .cover-preview {
      height: 250px;
    }
    
    .form-actions {
      flex-direction: column;
    }
    
    .form-actions button {
      width: 100%;
    }
    
    .modal-content {
      width: 95%;
      padding: 15px;
    }
    
    .chapter-item {
      flex-direction: column;
      align-items: flex-start;
    }
    
    .chapter-actions {
      margin-top: 10px;
      align-self: flex-end;
    }
  }
  </style>
